import { getKeyByItemIncludes } from '../helpers/object';
import {
  DebtTransferInvoice,
  DjangoDebtTransferInvoice,
  parseDebtTransferInvoice,
} from '../invoice';
import {
  parsePaycheckAdvanceBonus,
  PaycheckAdvanceBonus,
} from '../paycheck-advance/paycheck-advance-bonus.model';
import { DjangoTimeStamped, TimeStamped } from './../django';
import { parseDate } from './../helpers/date';
import { parseSimulation, Simulation } from './../simulation';
import { PROPOSAL_OPERATION_TYPE } from './proposal-operation-type.enum';
import {
  DjangoProposalRegistration,
  parseProposalRegistration,
  ProposalRegistration,
} from './proposal-registration.model';
import {
  DjangoProposalSigners,
  parseProposalSigners,
  ProposalSigners,
} from './proposal-signers.model';
import {
  PROPOSAL_APPROVABLE_STATUSES,
  PROPOSAL_CANCELABLE_STATUSES,
  PROPOSAL_FINANCIAL_CONSULTANT_APPROVAL_STATUSES,
  PROPOSAL_INVOICE_ATTACHABLE_STATUSES,
  PROPOSAL_INVOICE_DOWNLOADABLE_STATUSES,
  PROPOSAL_LENDER_SIGNABLE_STATUSES,
  PROPOSAL_SIGNABLE_STATUSES,
  PROPOSAL_STATUS,
} from './proposal-status.model';
import {
  EMPLOYEE_PROPOSAL_STATUS_BY_STEP,
  EMPLOYEE_PROPOSAL_STEP,
  HR_PROPOSAL_STATUS_BY_STEP,
  HR_PROPOSAL_STEP,
} from './proposal-steps.model';
import { PROPOSAL_TYPE } from './proposal-type.enum';

export class Proposal implements TimeStamped {
  id: string;
  transferedDebtAmount: number;
  documentKey: string;
  simulation: Simulation;
  registration: ProposalRegistration;
  proposalType: PROPOSAL_TYPE;
  operationType: PROPOSAL_OPERATION_TYPE;
  processingStatus: PROPOSAL_STATUS;
  bucket: string;
  key: string;
  debtTransfer: DebtTransferInvoice;
  signers: ProposalSigners;
  lenderPortfolio: string;
  paycheckAdvanceBonus: PaycheckAdvanceBonus;
  isRefi: boolean;
  ccbNumber: string;
  // DJANGO TIMESTAMPED
  modified: Date;
  created: Date;

  constructor(data: string | Proposal | DjangoProposal = {}) {
    if (typeof data === 'string') {
      bindConstructorDataToInstance(this, { id: data });
    } else {
      bindConstructorDataToInstance(this, data);
    }
  }

  get attachable(): boolean {
    return attachable(this);
  }

  get invoiceDownloadable(): boolean {
    return invoiceDownloadable(this);
  }

  get approvable(): boolean {
    return approvable(this);
  }

  get cancelable(): boolean {
    return cancelable(this);
  }

  get verifiable(): boolean {
    return verifiable(this);
  }

  get assignable(): boolean {
    return assignable(this);
  }

  get lenderSignable(): boolean {
    return lenderSignable(this);
  }

  get employeeStep(): EMPLOYEE_PROPOSAL_STEP {
    return getKeyByItemIncludes<EMPLOYEE_PROPOSAL_STEP>(
      EMPLOYEE_PROPOSAL_STATUS_BY_STEP,
      this.processingStatus,
    );
  }

  get hrStep(): HR_PROPOSAL_STEP {
    return getKeyByItemIncludes<HR_PROPOSAL_STEP>(
      HR_PROPOSAL_STATUS_BY_STEP,
      this.processingStatus,
    );
  }
}

export interface DjangoProposal extends DjangoTimeStamped {
  id?: string;
  transfered_debt_amount?: number;
  document_key?: string;
  simulation?: string | DjangoProposal;
  registration?: string | DjangoProposalRegistration;
  proposal_type?: PROPOSAL_TYPE;
  operation_type?: PROPOSAL_OPERATION_TYPE;
  processing_status?: PROPOSAL_STATUS;
  bucket?: string;
  key?: string;
  debt_transfer?: DjangoDebtTransferInvoice;
  signers?: DjangoProposalSigners;
  lender_portfolio?: string;
  paycheck_advance_bonus?: PaycheckAdvanceBonus;
  is_refi?: boolean;
  ccb_number?: string;
  // DJANGO TIMESTAMPED
  modified?: string;
  created?: string;
}

export function parseProposal(data: string | DjangoProposal | Proposal) {
  if (data) {
    return new Proposal(data);
  } else {
    return undefined;
  }
}

///////////////
/// PRIVATE ///
///////////////
function bindConstructorDataToInstance(instance: Proposal, data: Proposal | DjangoProposal) {
  data = bindDjangoPropsToJs(data) as Proposal;

  instance.id = data.id;
  instance.transferedDebtAmount = data.transferedDebtAmount;
  instance.documentKey = data.documentKey;
  instance.simulation = parseSimulation(data.simulation);
  instance.registration = parseProposalRegistration(data.registration);
  instance.proposalType = data.proposalType;
  instance.operationType = data.operationType;
  instance.processingStatus = data.processingStatus;
  instance.bucket = data.bucket;
  instance.key = data.key;
  instance.debtTransfer = parseDebtTransferInvoice(data.debtTransfer);
  instance.signers = parseProposalSigners(data.signers);
  instance.lenderPortfolio = data.lenderPortfolio;
  instance.paycheckAdvanceBonus = parsePaycheckAdvanceBonus(data.paycheckAdvanceBonus);
  instance.isRefi = data.isRefi;
  instance.ccbNumber = data.ccbNumber;
  // DJANGO TIMESTAMPED
  instance.modified = parseDate(data.modified);
  instance.created = parseDate(data.created);
}

function bindDjangoPropsToJs(data: Proposal | DjangoProposal) {
  const djangoData = data as DjangoProposal;
  const jsData = data as Proposal;
  return {
    ...data,
    transferedDebtAmount: djangoData.transfered_debt_amount || jsData.transferedDebtAmount,
    documentKey: djangoData.document_key || jsData.documentKey,
    proposalType: djangoData.proposal_type || jsData.proposalType,
    operationType: djangoData.operation_type || jsData.operationType,
    processingStatus: djangoData.processing_status || jsData.processingStatus,
    debtTransfer: djangoData.debt_transfer || jsData.debtTransfer,
    lenderPortfolio: djangoData.lender_portfolio || jsData.lenderPortfolio,
    paycheckAdvanceBonus: djangoData.paycheck_advance_bonus || jsData.paycheckAdvanceBonus,
    isRefi: djangoData.is_refi || jsData.isRefi,
    ccbNumber: djangoData.ccb_number || jsData.ccbNumber,
  };
}

function attachable(proposal: Proposal): boolean {
  return PROPOSAL_INVOICE_ATTACHABLE_STATUSES.includes(proposal.processingStatus);
}

function invoiceDownloadable(proposal: Proposal): boolean {
  const debtTransfer = proposal.operationType === PROPOSAL_OPERATION_TYPE.DEBT_TRANSFER;
  const downloadableStatus = PROPOSAL_INVOICE_DOWNLOADABLE_STATUSES.includes(
    proposal.processingStatus,
  );
  return debtTransfer && downloadableStatus;
}

function approvable(proposal: Proposal): boolean {
  return PROPOSAL_APPROVABLE_STATUSES.includes(proposal.processingStatus);
}

function cancelable(proposal: Proposal): boolean {
  return PROPOSAL_CANCELABLE_STATUSES.includes(proposal.processingStatus);
}

function assignable(proposal: Proposal): boolean {
  return PROPOSAL_SIGNABLE_STATUSES.includes(proposal.processingStatus);
}

function lenderSignable(proposal: Proposal): boolean {
  return PROPOSAL_LENDER_SIGNABLE_STATUSES.includes(proposal.processingStatus);
}

function verifiable(proposal: Proposal): boolean {
  return PROPOSAL_FINANCIAL_CONSULTANT_APPROVAL_STATUSES.includes(proposal.processingStatus);
}
